import { useState, useEffect } from 'react';
import { Layout } from '@/components/ui/Layout';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { supabase, createUser } from '@/lib/supabase';
import { Building, Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { ErrorDisplay } from '@/components/ui/ErrorDisplay';

interface UserWithOrg {
  id: string;
  email: string;
  organization: {
    id: string;
    name: string;
  } | null;
  created_at: string;
}

interface Organization {
  id: string;
  name: string;
}

export function Users() {
  const [users, setUsers] = useState<UserWithOrg[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithOrg | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    organizationId: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadUsers();
    loadOrganizations();
  }, []);

  async function loadUsers() {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Use left join to get all users, even those without organizations
      const { data: usersWithOrgs, error: usersError } = await supabase
        .from('users')
        .select(`
          id,
          email,
          created_at,
          organization_users!left (
            organizations (
              id,
              name
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (usersError) {
        throw new Error(`Failed to load users: ${usersError.message}`, { cause: usersError });
      }

      const transformedUsers: UserWithOrg[] = (usersWithOrgs || []).map(user => ({
        id: user.id,
        email: user.email,
        created_at: user.created_at,
        organization: user.organization_users?.[0]?.organizations || null
      }));

      setUsers(transformedUsers);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err instanceof Error ? err : new Error('Failed to load users'));
    } finally {
      setLoading(false);
    }
  }

  async function loadOrganizations() {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name');

      if (error) {
        throw new Error(`Failed to load organizations: ${error.message}`, { cause: error });
      }
      setOrganizations(data || []);
    } catch (err) {
      console.error('Error loading organizations:', err);
      setError(err instanceof Error ? err : new Error('Failed to load organizations'));
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingUser) {
        if (formData.organizationId) {
          const { error: deleteError } = await supabase
            .from('organization_users')
            .delete()
            .eq('user_id', editingUser.id);

          if (deleteError) {
            throw new Error(`Failed to update organization association: ${deleteError.message}`, { cause: deleteError });
          }

          const { error: insertError } = await supabase
            .from('organization_users')
            .insert({
              user_id: editingUser.id,
              organization_id: formData.organizationId,
              role: 'user'
            });

          if (insertError) {
            throw new Error(`Failed to create organization association: ${insertError.message}`, { cause: insertError });
          }
        }
      } else {
        // Create new user
        const { user, error: createError } = await createUser(
          formData.email,
          formData.password,
          formData.organizationId
        );

        if (createError) {
          throw new Error(createError);
        }
        if (!user) {
          throw new Error('Failed to create user');
        }
      }

      await loadUsers();
      setShowModal(false);
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        organizationId: ''
      });
      setSuccess(editingUser ? 'User updated successfully' : 'User created successfully');
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err instanceof Error ? err : new Error('Failed to save user'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (user: UserWithOrg) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      organizationId: user.organization?.id || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string, email: string) => {
    // Prevent deletion of admin user
    if (email.endsWith('@admin.com')) {
      setError(new Error('The admin user cannot be deleted'));
      return;
    }

    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
      setError(null);
      setSuccess(null);

      // Call the SECURITY DEFINER function to delete the user
      const { error } = await supabase.rpc('delete_user', {
        target_user_id: id
      });

      if (error) {
        throw new Error(`Failed to delete user: ${error.message}`, { cause: error });
      }

      await loadUsers();
      setSuccess('User deleted successfully');
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err instanceof Error ? err : new Error('Failed to delete user. Please ensure you have admin privileges.'));
    }
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Users</h1>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="h-5 w-5 mr-2" />
            Add User
          </Button>
        </div>

        {error && (
          <ErrorDisplay 
            error={error}
            context="User Management"
            onRetry={loadUsers}
            className="mb-4"
          />
        )}

        {success && (
          <div className="mb-4 bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded">
            {success}
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Organization
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Joined
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.organization ? (
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <Building className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
                        {user.organization.name}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">No organization</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(user)}
                        title="Edit user"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(user.id, user.email)}
                        className={`text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 ${
                          user.email.endsWith('@admin.com') ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        disabled={user.email.endsWith('@admin.com')}
                        title={user.email.endsWith('@admin.com') ? 'Admin user cannot be deleted' : 'Delete user'}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                {editingUser ? 'Edit User' : 'Add User'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    disabled={!!editingUser}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                  />
                </div>
                {!editingUser && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Password
                    </label>
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Organization
                  </label>
                  <select
                    value={formData.organizationId}
                    onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">No Organization</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end space-x-2 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowModal(false);
                      setEditingUser(null);
                      setFormData({
                        email: '',
                        password: '',
                        organizationId: ''
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}